/**
 * Carmen auth client singleton.
 *
 * Sessions are stored under `carmen-auth-v2`. Until the Supabase
 * decommission (2026-08-24) this module also carried a boot-time bridge that
 * traded a leftover supabase-js session for a Carmen one; the backend
 * endpoint behind it is gone, so all that remains is cleaning the dead key
 * out of localStorage.
 */
import { createAuthClient, webLocalStorageAdapter, type AuthClient } from "@carmen/auth-client";
import { API_URL } from "./env";

export const carmenAuth: AuthClient = createAuthClient({
  apiUrl: API_URL,
  storage: webLocalStorageAdapter(),
  storageKey: "carmen-auth-v2",
});

/** localStorage key the retired supabase-js client persisted its session under. */
const LEGACY_SUPABASE_STORAGE_KEY = "carmen-auth-react";

interface LegacyStorage {
  removeItem(key: string): void;
}

/**
 * Drops the retired supabase-js session key. Browsers that were last used
 * before the cutover still carry it; it is inert (nothing reads it any more)
 * but it holds a real access token, so it should not sit in localStorage
 * indefinitely. Best-effort and idempotent.
 */
export function clearLegacySupabaseSession(
  storage: LegacyStorage = globalThis.localStorage,
): void {
  try {
    storage.removeItem(LEGACY_SUPABASE_STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode, blocked cookies) — nothing to do.
  }
}
